import React, { useEffect, useState } from 'react';
import { teamMembers, advisors, TeamMember } from '../data/team';

// ── Toggle: set to false to restore separate "Partners" / "Interns" sections ──
const COMBINE_SECTIONS = true;

const TeamPage: React.FC = () => {
    const [archiveOpen, setArchiveOpen] = useState(() => {
        const hash = window.location.hash.substring(1);
        return !!hash && teamMembers.some(m => m.isArchived && !m.isHidden && m.id === hash);
    });

    useEffect(() => {
        const hash = window.location.hash.substring(1);
        if (hash) {
            setTimeout(() => {
                const element = document.getElementById(hash);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        }
    }, []);

    const activeMembers = teamMembers.filter(m => !m.isHidden && !m.isArchived);
    const archivedMembers = teamMembers.filter(m => m.isArchived && !m.isHidden);

    const partners = activeMembers.filter(m => m.category === 'partner');
    const interns = activeMembers.filter(m => m.category === 'intern');
    const combinedMembers = [...interns, ...partners];

    const renderMemberCard = (member: TeamMember) => {
        const cardContent = (
            <>
                <div className="h-32 md:h-40 bg-accent flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold">{member.imagePlaceholder}</span>
                </div>
                <div className="p-4 md:p-6 flex-1">
                    <h3 className="text-lg md:text-xl font-bold mb-1">{member.name}</h3>
                    <p className="text-accent-light mb-3 md:mb-4 text-sm md:text-base">{member.title}</p>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed">{member.bio}</p>
                </div>
            </>
        );

        const cardClasses =
            "bg-gray-900 bg-opacity-50 rounded-lg overflow-hidden transform transition-all hover:scale-105 scroll-mt-24 flex flex-col";

        if (member.linkedin) {
            return (
                <a
                    key={member.id}
                    id={member.id}
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cardClasses}
                >
                    {cardContent}
                </a>
            );
        }

        return (
            <div key={member.id} id={member.id} className={cardClasses}>
                {cardContent}
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-24">
            <h1 className="text-4xl md:text-5xl font-bold mb-12 tracking-tighter">
                Our <span className="text-accent" style={{
                    color: '#3F013D',
                    textShadow: '1px 1px 0 #ae4094, -1px -1px 0 #000, 0px 1px 0 #ae4094, 1px 0px 0 #ae4094, 0px -1px 0 #000, -1px 0px 0 #000'
                }}>Team</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-16 max-w-3xl">
                We've assembled a team of industry experts with deep experience in technology,
                finance, and operational excellence.
            </p>

            {COMBINE_SECTIONS ? (
                /* ── Combined view: interns first, then partners ── */
                <div className="mb-16">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {combinedMembers.map(renderMemberCard)}
                    </div>
                </div>
            ) : (
                /* ── Separate sections view ── */
                <>
                    <div className="mb-16">
                        {/* <h2 className="text-3xl font-bold mb-8">Partners</h2> */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {partners.map(renderMemberCard)}
                        </div>
                    </div>

                    <div className="mb-16">
                        <h2 className="text-3xl font-bold mb-8">Interns</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {interns.map(renderMemberCard)}
                        </div>
                    </div>
                </>
            )}

            {/* ── Member Archive (collapsible, before advisors) ── */}
            {archivedMembers.length > 0 && (
                <div className="mb-16">
                    <button
                        onClick={() => setArchiveOpen(!archiveOpen)}
                        className="flex items-center gap-3 text-2xl font-bold text-gray-400 hover:text-white transition-colors group"
                    >
                        <span
                            className={`inline-block transition-transform duration-200 text-base ${archiveOpen ? 'rotate-90' : ''}`}
                        >
                            ▶
                        </span>
                        Member Archive
                    </button>

                    {archiveOpen && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-8">
                            {archivedMembers.map(renderMemberCard)}
                        </div>
                    )}
                </div>
            )}

            {/* ── Advisors ── */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold mb-8">Advisors</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {advisors.map((advisor, index) => (
                        <div
                            key={index}
                            id={advisor.id}
                            className="flex items-center space-x-3 md:space-x-4 bg-gray-900 bg-opacity-30 p-3 md:p-4 rounded-lg scroll-mt-24"
                        >
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                                <span className="font-bold text-sm md:text-base">{advisor.name.split(' ').map(n => n[0]).join('')}</span>
                            </div>
                            <div>
                                <h3 className="font-bold text-sm md:text-base">{advisor.name}</h3>
                                <p className="text-gray-400 text-xs md:text-sm">{advisor.expertise}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-24 bg-gray-900 bg-opacity-50 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6">Join Our Team</h2>
                <p className="text-gray-300 mb-6">
                    We're always looking for exceptional talent to join our team. If you're passionate about technology,
                    finance, and building the future of software, we'd love to hear from you.
                </p>
                <a
                    href="/careers"
                    className="inline-block py-3 px-6 bg-accent text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors"
                >
                    View Open Positions
                </a>
            </div>
        </div>
    );
};

export default TeamPage;
